import { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { isEqual, isEmpty } from 'lodash'
import moment from 'moment'

import { enqueueSnackbar } from 'notistack'
import {
	Box,
	Dialog,
	DialogContent,
	Button,
	IconButton,
	Typography,
	TextField,
	Tooltip,
	CircularProgress,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { LoadingButton } from '@mui/lab'
import { Close, Delete, FileCopy, History, Reply, Visibility, Edit } from '@mui/icons-material'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import { IconThemeProvider, IconThemeContext } from 'custom-components/context/IconThemesContext'
import {
	isJson,
	logErrorMessage,
	convertArrayToObject,
} from '../../../../../../../utils/functions/helpers'
import { tileKeys } from '../../../../hooks/useTileQuery'
import { ENV_ACTIONS } from 'reducers/environmentReducer'
import { EVENT_STATUS } from '../../../dispatch-scheduler/DispatchSchedulerTile'
import { ACTION_TYPE } from '../../../dispatch-scheduler/helper/constants'

import { toDayjs } from 'utils/functions/doformsDateUtil'
import {
	getDevices,
	getDispatchSettings,
	updateAppointments,
} from 'components/core/services/environmentService'

export const useStyles = makeStyles(() => ({
	dialog: {
		'& .MuiButton-root': {
			textTransform: 'none !important',
		},
	},
	dialogTitle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		textAlign: 'center',
	},
	eventRenderer: {
		'& .MuiPaper-root': {
			marginTop: '-4px',
			minHeight: '25px',
			height: '96%',
		},
		'& .rs_event_item': {
			marginTop: '-4px',
		},
	},
	schedulerContainer: {
		'& .MuiPaper-root': {
			marginTop: '-3px',
			minHeight: '25px',
		},
		'& .rs__event__item': {
			marginTop: '2px',
			right: '3px',
		},
		'& .MuiPaper-root:has(> div:empty)': {
			display: 'none !important',
		},
	},
	buttonDialog: {
		'& .MuiButtonBase-root': {
			padding: '5px 1px !important',
		},
	},
	icon: (props) => ({
		color: props.color,
		'&:hover': {
			color: props.active.color,
			backgroundColor: 'transparent',
		},
	}),
	exitConfirmation: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		textAlign: 'center',
		'& .MuiButton-root': {
			textTransform: 'none !important',
		},
	},
	paper: {
		position: 'absolute',
		padding: '16px 32px 24px',
		borderRadius: '5px',
	},
	hidden: {
		display: 'none',
	},
	eventTypographyCenter: {
		display: 'flex',
		alignItems: 'center',
	},
}))

export const SchedulerCustomViewer = (props) => {
	const {
		isFromScheduler = false,
		selectedEvent,
		setSelectedEvent,
		event,
		close,
		open,
		hourFormat,
		mobileUnits,
		setAlertInfo,
		setOpenCustomEditor,
		setAction,
		refetchAppointments,
	} = props
	const [t] = useTranslation()
	const { environment } = useSelector((state) => state)
	const { iconTheme } = useContext(IconThemeContext)
	const classes = useStyles(iconTheme)
	// const [locale, setLocale] = useState(enUS)

	const [isSaving, setIsSaving] = useState(false)
	const [eventStatus, setEventStatus] = useState(event.dispatch?.status)
	const [eventUser, setEventUser] = useState(event.dispatch?.ownerId)
	const [eventTitle, setEventTitle] = useState(event.title)
	const [eventStartTime, setEventStartTime] = useState(toDayjs(event.start))
	const [eventEndTime, setEventEndTime] = useState(toDayjs(event.end))

	useEffect(() => {
		if (!isFromScheduler || isEqual(selectedEvent, event)) return

		setSelectedEvent(event)
	}, [isFromScheduler, selectedEvent])

	const eventEditable =
		event?.dispatch?.status?.toLowerCase() === EVENT_STATUS.PENDING ||
		event?.dispatch?.status?.toLowerCase() === EVENT_STATUS.SCHEDULED
	const maxLength = 56
	const isLongText = eventTitle.length > maxLength

	const handleSaveEvent = async () => {
		if (!eventStartTime || !eventEndTime) {
			enqueueSnackbar('Invalid time values.', { variant: 'error' })
			return
		}

		const duration = eventEndTime.diff(eventStartTime, 'minutes')
		if (duration <= 0) {
			enqueueSnackbar('Start time must be before end time.', { variant: 'error' })
			return
		}

		setIsSaving(true)
		try {
			const dateString = moment(event.start).format('YYYY-MM-DD')
			const timeString = eventStartTime.format('HH:mm')
			const payload = {
				date: dateString,
				time: timeString,
				duration,
			}

			await updateAppointments(environment.apiToken, event.event_id, payload)

			enqueueSnackbar('Updated successfully.', { variant: 'success' })

			if (refetchAppointments) {
				refetchAppointments()
			}
			close()
		} catch (err) {
			const errorMessage = 'Code ' + err.response?.data?.code + ': ' + err.response?.data?.message

			setAlertInfo({
				open: true,
				type: 'error',
				text: errorMessage,
			})
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<Dialog open={open} onClose={close}>
			{isSaving && (
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						backgroundColor: 'rgba(255,255,255,0.5)',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 10,
					}}
				>
					<CircularProgress size={50} />
				</div>
			)}
			<div style={{ backgroundColor: event.color, minHeight: '40px' }}></div>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '8px',
					paddingRight: '8px',
				}}
			>
				<IconButton size="small" onClick={close}>
					<Close />
				</IconButton>
				<div className={classes.buttonDialog} style={{ display: 'flex' }}>
					{event.availableActions?.includes(ACTION_TYPE.HISTORY) && (
						<Tooltip title="History">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.HISTORY)
									setOpenCustomEditor(true)
								}}
							>
								<History />
							</IconButton>
						</Tooltip>
					)}
					{event.availableActions?.includes(ACTION_TYPE.VIEW) && (
						<Tooltip title="View">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.VIEW)
									setOpenCustomEditor(true)
								}}
							>
								<Visibility />
							</IconButton>
						</Tooltip>
					)}
					{event.availableActions?.includes(ACTION_TYPE.RECALL) && (
						<Tooltip title="Recall">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.RECALL)
									setOpenCustomEditor(true)
								}}
							>
								<Reply />
							</IconButton>
						</Tooltip>
					)}
					{event.availableActions?.includes(ACTION_TYPE.COPY) && (
						<Tooltip title="Copy">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.COPY)
									setOpenCustomEditor(true)
								}}
							>
								<FileCopy />
							</IconButton>
						</Tooltip>
					)}
					{event.availableActions?.includes(ACTION_TYPE.DELETE) && (
						<Tooltip title="Delete">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.DELETE)
									setOpenCustomEditor(true)
								}}
							>
								<Delete />
							</IconButton>
						</Tooltip>
					)}
					{event.availableActions?.includes(ACTION_TYPE.EDIT) && (
						<Tooltip title="Edit">
							<IconButton
								size="small"
								onClick={() => {
									setAction(ACTION_TYPE.EDIT)
									setOpenCustomEditor(true)
								}}
							>
								<Edit />
							</IconButton>
						</Tooltip>
					)}
				</div>
			</div>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: '5px' }}>
					<TextField
						id="eventStatus"
						value={eventStatus}
						onChange={(e) => setEventStatus(e.target.value)}
						label={t('common:misc.status')}
						size="small"
						variant="outlined"
						disabled={true}
					/>
					<FormControl size="small" variant="outlined" fullWidth>
						<InputLabel id="datagrid-select-label">{t('common:nav.user')}</InputLabel>
						<Select
							labelId="datagrid-select-label"
							id="datagrid-select-small"
							value={eventUser}
							onChange={(e) => {
								setEventUser(e.target.value)
							}}
							disabled={!eventEditable}
							label={t('common:nav.user')}
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 300, // Giới hạn chiều cao dropdown
										width: 300, // Giới hạn chiều rộng dropdown
										overflow: 'auto',
									},
								},
							}}
						>
							{mobileUnits.map((mb) => (
								<MenuItem value={mb.number} key={mb.number}>
									{mb.number + ' - ' + mb.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Tooltip
						title={
							isLongText ? (
								<Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{eventTitle}</Typography>
							) : (
								''
							)
						}
						arrow
					>
						<TextField
							id="eventTitle"
							value={eventTitle}
							onChange={(e) => setEventTitle(e.target.value)}
							label={t('common:misc.recordName')}
							size="small"
							variant="outlined"
							disabled={true}
						/>
					</Tooltip>
					<div></div>
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							gap: '8px',
						}}
					>
						<Typography variant="body1" color="textSecondary" paddingTop="17px">
							📅 {moment(event.start).format('MM/DD/YYYY')}
						</Typography>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<TimePicker
								label={t('common:misc.startTimeScheduler')}
								value={toDayjs(eventStartTime)}
								onChange={(newValue) => setEventStartTime(newValue)}
								sx={{ width: '150px' }}
								disabled={!eventEditable}
								ampm={hourFormat === '12'}
								format={hourFormat === '12' ? 'hh:mm A' : 'HH:mm'}
							/>
						</LocalizationProvider>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<TimePicker
								label={t('common:misc.endTimeScheduler')}
								value={toDayjs(eventEndTime)}
								onChange={(newValue) => setEventEndTime(newValue)}
								sx={{ width: '150px' }}
								disabled={!eventEditable}
								ampm={hourFormat === '12'}
								format={hourFormat === '12' ? 'hh:mm A' : 'HH:mm'}
							/>
						</LocalizationProvider>
					</div>
					{eventEditable && (
						<div
							style={{
								display: 'flex',
								justifyContent: 'end',
								gap: '8px',
							}}
						>
							<Button onClick={handleSaveEvent} disabled={isSaving} sx={{ fontSize: '18px' }}>
								{t('common:misc.saveAndClose')}
							</Button>
						</div>
					)}
				</Box>
			</DialogContent>
		</Dialog>
	)
}
